import { message, Table } from 'antd'
import React, { useState } from 'react'
import dayjs from 'dayjs'
import { Box, Grid, IconButton, Typography } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import VpnKeyIcon from '@mui/icons-material/VpnKey'
import EditIcon from '@mui/icons-material/Edit'
import { useDispatch } from 'react-redux'
import { IFilterMapped } from './CustomerBlackListFilter'
import { ICustomerBlackList } from '../../../../../types/customerBlackList'
import { useUser } from '../../../../../hooks/useUser'
import { axiosInstance } from '../../../../../hooks/useAxios'
import CircularLoading from '../../../../../components/CircularProgress'
import Pagination from '../../../../../components/Pagination'
import {
  ONLY_DATE_FORMAT,
  ONLY_TIME_FORMAT,
} from '../../../../../constants/global'
import { openModal } from '../../../../../../store/reducers/modal/modal.slice'
import { CustomerBlackListFormModal } from './CustomerBlackListFormModal'
import { ERRORS_MESSAGE_MAPPER } from '../../../../../constants/errors'
import { Tag } from '../../../../../components/Tag'

interface ICustomerBlackListTableProps {
  isLoading: boolean
  data: ICustomerBlackList[]
  onUpdateCustomerBlackListSuccess: () => void
  filter: IFilterMapped
  onFilterChange: (filter: IFilterMapped) => void
  handlePageChange: (filter: IFilterMapped) => void
  total: number
}

const CustomerBlackListTable = ({
  isLoading,
  data,
  onUpdateCustomerBlackListSuccess,
  filter,
  onFilterChange,
  handlePageChange,
  total,
}: ICustomerBlackListTableProps) => {
  const dispatch = useDispatch()
  const { user } = useUser()
  const [isModalEditCustomerBlackList, setIsModalEditCustomerBlackList] =
    useState<boolean>(false)
  const [customerBlackListSelect, setCustomerBlackListSelect] =
    useState<ICustomerBlackList>()

  const onHandleDeleteCustomerBlackList = async (objectId: string) => {
    try {
      const response = await axiosInstance.delete(`/blacklists/${objectId}`)
      if (response.status === 200) {
        message.success('ลบข้อมูลสำเร็จ')
        onUpdateCustomerBlackListSuccess()
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      width: 80,
      align: 'center' as const,
      render: (numberRow: number) => {
        return (
          <Typography
            variant="subtitle1"
            color="text.secondary"
            textAlign={'center'}
          >
            {numberRow}
          </Typography>
        )
      },
    },
    {
      title: 'ชื่อ-สกุล',
      dataIndex: 'name',
      key: 'name',
      align: 'left' as const,
      render: (name: string) => {
        return (
          <Typography variant="subtitle2" color="text.secondary" minWidth={200}>
            {name}
          </Typography>
        )
      },
    },
    {
      title: 'เบอร์ติดต่อ',
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      align: 'center' as const,
      render: (phoneNumber: any) => {
        return (
          <Typography variant="subtitle2" color="text.secondary" minWidth={80}>
            {phoneNumber}
          </Typography>
        )
      },
    },
    {
      title: 'สถานะ',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center' as const,
      render: (isActive: boolean) => {
        return (
          <Tag color={isActive ? 'success' : 'error'}>
            {isActive ? 'เปิด' : 'ปิด'}
          </Tag>
        )
      },
    },
    {
      title: 'วันที่เพิ่ม',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      align: 'center' as const,
      render: (createdAt: string) => {
        return (
          <Grid container flexDirection={'column'}>
            <Grid item>
              <Typography variant="subtitle2" color="text.secondary">
                {dayjs(createdAt).format(ONLY_DATE_FORMAT)}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="caption" color="text.secondary">
                {dayjs(createdAt).format(ONLY_TIME_FORMAT)}
              </Typography>
            </Grid>
          </Grid>
        )
      },
    },
    {
      dataIndex: '',
      key: 'action',
      align: 'center' as const,
      width: 170,
      render: (_: undefined, customerBlackListData: ICustomerBlackList) => {
        return (
          <Grid container spacing={2} width={170}>
            <Grid item>
              <IconButton
                onClick={() =>
                  dispatch(
                    openModal({
                      title: 'สร้างลูกค้า Black List',
                      content: (
                        <CustomerBlackListFormModal
                          customerBlackList={customerBlackListData}
                          onUpdateCustomerBlackListSuccess={
                            onUpdateCustomerBlackListSuccess
                          }
                        />
                      ),
                    }),
                  )
                }
              >
                <EditIcon />
              </IconButton>
            </Grid>
            <Grid item>
              <IconButton
                onClick={() =>
                  onHandleDeleteCustomerBlackList(
                    customerBlackListData.objectId!,
                  )
                }
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        )
      },
    },
  ]

  return (
    <>
      <CircularLoading isLoading={isLoading} />
      <Table
        size={'middle'}
        columns={columns}
        scroll={{
          x: columns.reduce(
            (pre, cur: any) => (cur.width ? cur.width + pre : pre),
            0,
          ),
        }}
        dataSource={data?.map(
          (customerBlackListData: ICustomerBlackList, index: number) => {
            return {
              ...customerBlackListData,
              key: customerBlackListData.objectId,
              numberRow:
                index + 1 + ((filter.page || 0) - 1) * (filter.perPage || 0),
            }
          },
        )}
        bordered
        pagination={false}
      />
      <Pagination
        total={total}
        current={filter.page!}
        pageSize={filter.perPage!}
        onChange={(page, perPage) => {
          handlePageChange({ ...filter, page, perPage })
        }}
      />
    </>
  )
}

export default CustomerBlackListTable
